import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Modal, message } from 'antd';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { HasResource } from '../../../components/HasResource';
import { ResourceCode } from 'constants/resourceCodes';
import { hasResource } from 'utils/resourceAuth';
import FilterBar from './components/FilterBar';
import AoiListPanel from './components/AoiListPanel';
import MapCanvas from './components/MapCanvas';
import MapToolbar from './components/MapToolbar';
import Legend from './components/Legend';
import AoiDrawer from './components/AoiDrawer';
import { useAoiSelection } from './hooks/useAoiSelection';
import { useDrawController } from './hooks/useDrawController';
import {
    DEFAULT_ZOOM,
    FIT_MAX_ZOOM,
    DRAWER_WIDTH,
    LIST_CLICK_ZOOM,
    COORD_FLY_ZOOM,
    MIN_LOAD_ZOOM,
    LA_CENTER,
    LIST_PAGE_SIZE,
    AUDIT_LIST_PAGE_SIZE,
    produceErrorText,
    SourceGroup,
    toAoiIdNumber,
} from './constants';
import {
    BasemapType,
    DrawActions,
    DrawerPoint,
    EMPTY_FILTERS,
    FiltersState,
    GeoJsonGeometry,
    PickTarget,
    PickedPoint,
    SourceVisibility,
    isAuditStatusFilter,
    numericLevelToEnum,
    ViewCommand,
    ViewCommandInput,
    levelFilterToNumeric,
} from './types';
import { bboxOf, bboxOfAll, parseGeometry } from './utils/geometry';
import {
    BizAoiAttributeListV2Input,
    BizAoiAttributeListV2Item,
    LIST_AOI_ATTRIBUTES_V2,
    ListAoiAttributesV2Response,
} from './graphql/queries/listAoiAttributesV2';
import { DELETE_BIZ_AOI, DeleteBizAoiResponse } from './graphql/mutations/deleteBizAoi';
import { LIST_AOI_BIZ_TAG_DICTIONARY, ListAoiBizTagDictionaryResponse } from './graphql/queries/listAoiBizTagDictionary';
import { bizTagNameMap } from './utils/bizTag';
import { BizAoiProduceResult } from './graphql/mutations/produceResult';

/** 地图顶部居中的操作提示条 (antd Alert 语言, 不用原型的深色药丸)。 */
const HintPill: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div
        className="absolute left-1/2 flex items-center gap-3 rounded-md border bg-white px-4 py-2 text-xs shadow-md"
        style={{
            top: 12,
            transform: 'translateX(-50%)',
            zIndex: 1250,
            borderColor: '#91caff',
            background: '#e6f4ff',
            color: 'rgba(0,0,0,.88)',
            whiteSpace: 'nowrap',
        }}
    >
        {children}
    </div>
);

/**
 * AOI 生产管理平台 V2 - 单页 (筛选 + 列表 + 双引擎地图 + 属性抽屉)。
 *
 * <p>布局对齐产品原型: 顶部深色 header → 筛选区 → 左列表 / 中地图 / 右抽屉。
 */
const AoiPlatformV2Page: React.FC = () => {
    const canEdit = hasResource([ResourceCode.aoiPlatform.UPSERT]);

    /* ====================== 筛选 + 列表 ====================== */

    const [filters, setFilters] = useState<FiltersState>(EMPTY_FILTERS);
    const [listItems, setListItems] = useState<BizAoiAttributeListV2Item[]>([]);
    const [listTotal, setListTotal] = useState(0);
    const [searched, setSearched] = useState(false);
    // 默认收起: 进页面先给地图全宽 (未筛选时列表本来是空态), 需要时再展开
    const [listCollapsed, setListCollapsed] = useState(true);
    /** 绘制中的命令式操作出口 (由 MapCanvas 在 handler 就绪时填充)。 */
    const drawActionsRef = useRef<DrawActions | null>(null);
    /** 绘制中已点顶点数 (决定"撤销上一点 / 闭合成面"能不能点)。 */
    const [drawVertexCount, setDrawVertexCount] = useState(0);
    /** 当前地图 zoom (图例里解释分级加载, 免得被当成 bug)。 */
    const [mapZoom, setMapZoom] = useState<number | null>(null);

    const [runList, { loading: listLoading }] = useLazyQuery<ListAoiAttributesV2Response>(LIST_AOI_ATTRIBUTES_V2, {
        fetchPolicy: 'network-only',
        onCompleted: (data) => {
            setListItems(data?.listAoiAttributes?.items ?? []);
            setListTotal(data?.listAoiAttributes?.total ?? 0);
        },
        onError: (err) => {
            message.error(`查询列表失败: ${err.message}`);
        },
    });

    /**
     * 业务标签字典: 抽屉打标 / 筛选下拉 / 列表 chips 三处共用, 进页面拉一次 (字典是代码枚举, 改动靠发版)。
     * 加载失败只提示不阻塞: 标签区退化成只显示 code, 其余功能照常。
     */
    const { data: tagDictData, error: tagDictError, refetch: refetchTagDict } = useQuery<ListAoiBizTagDictionaryResponse>(LIST_AOI_BIZ_TAG_DICTIONARY, {
        fetchPolicy: 'cache-first',
        onError: (err) => {
            message.warning(`业务标签字典加载失败: ${err.message}`);
        },
    });
    const tagDictionary = useMemo(() => tagDictData?.listAoiBizTagDictionary ?? [], [tagDictData]);
    const tagNames = useMemo(() => bizTagNameMap(tagDictionary), [tagDictionary]);

    /* ====================== 地图 / 视口 ====================== */

    // 默认 OSM (2026-08-05 需求): 瓦片直连 CDN 立刻出图, 且不点卫星图/Google 就不触发 Google JS API 加载
    const [basemap, setBasemap] = useState<BasemapType>('osm');
    const [sourceVisibility, setSourceVisibility] = useState<SourceVisibility>({
        MANUAL: true,
        SURVEY: true,
        OVERTURE: true,
    });
    /**
     * 「未生效」图层开关 (V2.1 人工审核): 默认关 —— 瓦片请求与从前一模一样, 待审核面只在运营主动打开时才画出来。
     * 筛「未生效」/ 点到未生效的列表项时自动打开 (否则选中了却在地图上看不见)。
     */
    const [showUnverified, setShowUnverified] = useState(false);
    /**
     * 瓦片数据版本 + 本次失效范围: 写操作自增版本并带上编辑面的 bbox,
     * 瓦片层只对 bbox 压到的片换新 URL 冷拉, 其余继续命中浏览器缓存 ——
     * 否则"改一栋楼全屏缓存作废", 用户到服务器单程 300ms+, 每次保存后都要重新热一遍。
     * bbox 拿不到时传 null, 退回全局失效兜底。
     */
    const [tileInvalidation, setTileInvalidation] = useState<{
        version: number;
        bbox: [number, number, number, number] | null;
    }>({ version: 0, bbox: null });
    const invalidateTiles = useCallback((bbox?: [number, number, number, number] | null) => {
        setTileInvalidation((prev) => ({ version: prev.version + 1, bbox: bbox ?? null }));
    }, []);

    const viewNonceRef = useRef(0);
    const [viewCommand, setViewCommand] = useState<ViewCommand | null>(null);
    const issueViewCommand = useCallback((cmd: ViewCommandInput) => {
        viewNonceRef.current += 1;
        setViewCommand({ ...cmd, nonce: viewNonceRef.current } as ViewCommand);
    }, []);

    /** 点了"筛选"后待执行的地图跟随 (结果回来才知道飞哪, 用 flag 桥接)。 */
    const pendingListFitRef = useRef(false);
    useEffect(() => {
        if (!pendingListFitRef.current) {
            return;
        }
        pendingListFitRef.current = false;
        // 按结果中心点的包络框飞过去: zipcode 筛选时即"飘到这个 zipcode 的成片区域"
        const pts = listItems.filter((i) => i.centerLatitude != null && i.centerLongitude != null);
        if (pts.length === 0) {
            return;
        }
        const lats = pts.map((i) => i.centerLatitude as number);
        const lngs = pts.map((i) => i.centerLongitude as number);
        issueViewCommand({
            kind: 'fit',
            minLng: Math.min(...lngs),
            minLat: Math.min(...lats),
            maxLng: Math.max(...lngs),
            maxLat: Math.max(...lats),
            maxZoom: FIT_MAX_ZOOM,
        });
    }, [listItems, issueViewCommand]);

    /* ====================== 选中 + 详情 + 抽屉 ====================== */

    const selection = useAoiSelection();
    const { selected, detail, detailLoading, select, refetchDetail } = selection;

    // 选中面的权威几何来自 detail (瓦片是渲染用的二进制, 取不出几何);
    // detail 未回来时选中高亮层暂时空着, 瓦片图层自己已经把选中面描成级别加深色了。
    // 声明在各 handler 之前: 删除 / 保存要用它算瓦片失效范围。
    const selectedGeometry = useMemo<GeoJsonGeometry | null>(() => {
        // detail.aoiId 运行时是字符串 (Long 标量), 比对前归一
        if (!selected || detail == null || toAoiIdNumber(detail.aoiId) !== selected.aoiId) return null;
        return parseGeometry(detail.selfGeometry?.geometryGeoJson);
    }, [selected, detail]);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const pendingFitRef = useRef(false);

    const draw = useDrawController();

    const [pickTarget, setPickTarget] = useState<PickTarget>(null);
    const [pickedPoint, setPickedPoint] = useState<PickedPoint | null>(null);
    const pickNonceRef = useRef(0);
    const [drawerEntryPoints, setDrawerEntryPoints] = useState<DrawerPoint[]>([]);
    const [drawerParkingPoints, setDrawerParkingPoints] = useState<DrawerPoint[]>([]);

    const [conflictIds, setConflictIds] = useState<number[]>([]);
    /** 「飞到坐标」的落点标记 (纯视觉参照, 不参与任何业务逻辑; 重置筛选时清掉)。 */
    const [coordMarker, setCoordMarker] = useState<{ lat: number; lng: number } | null>(null);

    /* ====================== 全屏模式 ====================== */

    /**
     * 沉浸模式: 整个页面容器 position:fixed 铺满视口。
     *
     * <p><b>为什么不用浏览器原生 Fullscreen API</b>: 原生全屏下 antd 的浮层 (下拉 / DatePicker /
     * Modal) 默认挂在 body 上, 会跑到全屏元素之外变成看不见 —— 这个坑本页已经踩过一次
     * (抽屉内的浮层错位)。fixed 定位不改变 DOM 层级, 浮层照常工作。
     */
    const [fullscreen, setFullscreen] = useState(false);
    /** 全屏下是否仍显示筛选区 + 列表 (默认藏起来把空间全给地图, 需要时一键叫回来)。 */
    const [fullscreenChrome, setFullscreenChrome] = useState(false);
    /** 全屏时隐藏筛选/标题/列表; 非全屏一律显示。 */
    const chromeVisible = !fullscreen || fullscreenChrome;

    // Esc 退出全屏 (只在全屏时挂监听, 不给全局键盘加常驻负担)
    useEffect(() => {
        if (!fullscreen) {
            return undefined;
        }
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                setFullscreen(false);
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [fullscreen]);

    const [deleteMutation] = useMutation<DeleteBizAoiResponse>(DELETE_BIZ_AOI);

    // 详情加载完成后, 如果是"列表点击"触发的选中 → fitBounds 到该 AOI
    useEffect(() => {
        if (!pendingFitRef.current || !detail?.selfGeometry) return;
        const g = detail.selfGeometry;
        if (g.minLatitude != null && g.minLongitude != null && g.maxLatitude != null && g.maxLongitude != null) {
            issueViewCommand({
                kind: 'fit',
                minLng: g.minLongitude,
                minLat: g.minLatitude,
                maxLng: g.maxLongitude,
                maxLat: g.maxLatitude,
                maxZoom: FIT_MAX_ZOOM,
                minZoom: MIN_LOAD_ZOOM,
            });
            pendingFitRef.current = false;
        } else {
            const geom = parseGeometry(g.geometryGeoJson);
            const b = bboxOf(geom);
            if (b) {
                issueViewCommand({
                    kind: 'fit', minLng: b[0], minLat: b[1], maxLng: b[2], maxLat: b[3],
                    maxZoom: FIT_MAX_ZOOM, minZoom: MIN_LOAD_ZOOM,
                });
            }
            pendingFitRef.current = false;
        }
    }, [detail, issueViewCommand]);

    /* ====================== 筛选交互 ====================== */

    const handleSearch = useCallback(() => {
        // 结果要看得见: 列表默认是收起的, 一点筛选就展开 (含 loading 态)
        setListCollapsed(false);
        const zip = filters.zipcode.trim();
        const id = filters.id.trim();
        const hasFuzzy = !!(filters.name.trim() || filters.address.trim() || filters.lat.trim()
            || filters.lng.trim() || filters.createdFrom != null || filters.createdTo != null || filters.tagCode != null);
        let input: BizAoiAttributeListV2Input;
        if (!zip && id) {
            // 无 zipcode + 有 id: 按纯数字 aoiId 精确查
            if (!/^\d+$/.test(id)) {
                message.warning('无 Zipcode 时仅支持按纯数字 AOI ID 精确查询, 请填写 Zipcode');
                return;
            }
            if (hasFuzzy) {
                message.info('无 Zipcode 时其他筛选条件不生效, 仅按 AOI ID 精确查询');
            }
            // 生效状态不是模糊条件, 按 ID 精确查也带上 —— 否则未生效的面按 ID 搜不到 (后端默认只回已生效)
            input = {
                aoiId: Number(id),
                effectiveStatus: filters.effectiveStatus,
                level: levelFilterToNumeric(filters.level),
                offset: 0,
                limit: LIST_PAGE_SIZE,
            };
        } else if (!zip) {
            // 无 zipcode + 无 id: 只能按单值生效状态跨全部分区查 (运营找待审的面时不知道邮编 ——
            // 一批算法 2 级面动辄散在上百个 zipcode 里)。
            // 「全部」不行: 它等于不筛状态, 在一亿行的 biz_aoi 上没有任何业务意义。
            if (!isAuditStatusFilter(filters.effectiveStatus)) {
                message.warning('不填 Zipcode 时请选择具体的「生效状态」(未生效 / 已生效), 或填写 Zipcode / AOI ID');
                return;
            }
            if (hasFuzzy) {
                // 这几个框在无 Zipcode 时是置灰的, 但值可能是"先填了 Zipcode 再清掉"留下的残留
                message.info('无 Zipcode 时仅支持「生效状态」+「级别」筛选, 其余条件已忽略');
            }
            // 跨分区路径的筛选白名单: 只允许 生效状态 + 级别 (2026-09-21 契约收口)。
            // 这条路径唯一能用的索引前缀是 (bucket_no, 等值列), 进不去索引的条件只能退化成回表逐行过滤,
            // 代价与命中稀疏度成反比 —— 「已生效 + 2 级」恒定 5 秒超时就是这么来的;
            // 名称 / 经纬度 / 地址这类 LIKE '%kw%' 更是加任何索引都救不了。后端两层都会拒, 前端不再下发。
            input = {
                effectiveStatus: filters.effectiveStatus,
                level: levelFilterToNumeric(filters.level),
                offset: 0,
                limit: AUDIT_LIST_PAGE_SIZE,
            };
        } else {
            input = {
                zipcode: zip,
                aoiIdKeyword: id || null,
                nameKeyword: filters.name.trim() || null,
                addressKeyword: filters.address.trim() || null,
                latKeyword: filters.lat.trim() || null,
                lngKeyword: filters.lng.trim() || null,
                createdAtStart: filters.createdFrom,
                createdAtEnd: filters.createdTo,
                // 业务标签精确筛 (需 zipcode; 走 JSON_CONTAINS + has_biz_tag 标志列)
                tagCode: filters.tagCode,
                // 始终显式传: 「全部」= ALL 才含未生效; 单值 = 审核视图, 后端按 aoiId 升序, 单页 50 (PRD)
                effectiveStatus: filters.effectiveStatus,
                level: levelFilterToNumeric(filters.level),
                offset: 0,
                limit: isAuditStatusFilter(filters.effectiveStatus) ? AUDIT_LIST_PAGE_SIZE : LIST_PAGE_SIZE,
            };
        }
        if (filters.effectiveStatus === 'UNVERIFIED' && !showUnverified) {
            // 专门筛待审核面, 地图上却画不出来会被当成"没数据"; 自动打开并提示
            message.info('已自动开启「未生效」图层, 以显示待审核的 AOI');
            setShowUnverified(true);
        }
        setSearched(true);
        pendingListFitRef.current = true;
        void runList({ variables: { input } });
    }, [filters, runList, showUnverified]);

    /**
     * 「飞到坐标」: 纯地图导航 —— 直接 setView 到该点, 不查列表、不发任何请求。
     *
     * <p>与「筛选」里把经纬度当条件 (latKeyword/lngKeyword 模糊匹配, 用于定位建筑) 是两条独立能力,
     * 互不影响: 这里只动视野, 选中态 / 列表 / 草稿全不碰。落点 zoom 取 COORD_FLY_ZOOM (18),
     * 高于 BUILDING_MIN_ZOOM 保证落地就能看到 1 级楼栋轮廓。
     */
    const handleFlyToCoord = useCallback((lat: number, lng: number) => {
        issueViewCommand({ kind: 'set', lat, lng, zoom: COORD_FLY_ZOOM });
        setCoordMarker({ lat, lng });
    }, [issueViewCommand]);

    const handleReset = useCallback(() => {
        setFilters(EMPTY_FILTERS);
        setCoordMarker(null);
        setListItems([]);
        setListTotal(0);
        setSearched(false);
        setConflictIds([]);
        setDrawerOpen(false);
        select(null);
        draw.cancelDraw();
        draw.stopEdit();
        draw.clearDraft();
        draw.clearEdited();
        setPickTarget(null);
        issueViewCommand({ kind: 'set', lat: LA_CENTER.lat, lng: LA_CENTER.lng, zoom: DEFAULT_ZOOM });
    }, [select, draw, issueViewCommand]);

    /* ====================== 选中交互 ====================== */

    const handleSelectAoi = useCallback((aoiId: number, zipcode: string | null, createdAt?: string | null, fit?: boolean) => {
        // 入口归一: 列表点选传进来的 aoiId 运行时是字符串 (GraphQL Long), 瓦片点选是数字,
        // selected.aoiId 必须是稳定的数字, 否则下游所有 === 比对 (详情守卫/瓦片高亮/列表高亮) 随入口不同而随机失配
        const idNum = toAoiIdNumber(aoiId);
        if (!zipcode) {
            message.warning(`AOI-${idNum} 缺少 zipcode, 无法打开详情`);
            return;
        }
        // 切换选中: 丢弃未保存的形状改动 / 新建草稿
        draw.stopEdit();
        draw.clearEdited();
        draw.clearDraft();
        setPickTarget(null);
        pendingFitRef.current = !!fit;
        select({ aoiId: idNum, zipcode, createdAt: createdAt ?? null });
        setDrawerOpen(true);
    }, [draw, select]);

    const handleListItemClick = useCallback((item: BizAoiAttributeListV2Item) => {
        // 先用列表行自带的中心点立刻飞过去 (不等 detail), detail 回来后再用几何 bbox 精确 fit;
        // 详情失败 / 没有几何时也至少定位到点上, 不会出现"点了列表地图不动"。
        if (item.centerLatitude != null && item.centerLongitude != null) {
            issueViewCommand({
                kind: 'set',
                lat: item.centerLatitude,
                lng: item.centerLongitude,
                zoom: LIST_CLICK_ZOOM,
            });
        }
        if (item.status === 0 && !showUnverified) {
            // 未生效面只在开关打开时才在瓦片里; 不开的话"点了列表, 地图上却没有这个面"
            message.info('已自动开启「未生效」图层, 以显示该 AOI');
            setShowUnverified(true);
        }
        handleSelectAoi(item.aoiId, item.zipcode, item.createdAt, true);
    }, [handleSelectAoi, issueViewCommand, showUnverified]);

    /* ====================== 绘制 / 编辑形状 ====================== */

    const handleStartDraw = useCallback(() => {
        if (draw.mode === 'draw') {
            draw.cancelDraw();
            return;
        }
        setDrawerOpen(false);
        select(null);
        setPickTarget(null);
        setConflictIds([]);
        draw.startDraw();
    }, [draw, select]);

    const handleDrawComplete = useCallback((geometry: GeoJsonGeometry) => {
        draw.completeDraw(geometry);
        setDrawerOpen(true);
    }, [draw]);

    const handleToggleEditShape = useCallback(() => {
        if (draw.mode === 'edit') {
            draw.stopEdit();
            return;
        }
        // 草稿(刚画完还没保存)也能直接调顶点, 不用先存再选中
        if (!draw.draftGeometry && (!selected || !detail?.selfGeometry?.geometryGeoJson)) {
            message.warning('请先选中一个 AOI (且详情加载完成), 或画一个新面');
            return;
        }
        setPickTarget(null);
        // 编辑形状的改动是 pending 的, 唯一的保存入口在抽屉里 —— 抽屉关着就等于"改了却没地方保存"。
        // 从工具栏进入编辑时强制把抽屉打开, 保证保存按钮始终可见。
        setDrawerOpen(true);
        draw.startEdit();
    }, [draw, selected, detail]);

    /** 顶点编辑回流: 草稿写回草稿, 已存在的 AOI 记成 pending 改动 (等抽屉保存)。 */
    const handleEditChange = useCallback((geometry: GeoJsonGeometry) => {
        if (draw.draftGeometry) {
            draw.updateDraft(geometry);
            return;
        }
        draw.onEditChange(geometry);
    }, [draw]);

    /** 绘制中的撤销 / 闭合: handler 在 MapCanvas 内部, 通过 ref 调。 */
    const handleUndoVertex = useCallback(() => {
        drawActionsRef.current?.deleteLastVertex();
    }, []);

    const handleFinishDraw = useCallback(() => {
        drawActionsRef.current?.completeShape();
    }, []);

    /** 丢弃草稿: 只清前端状态, 没落库所以不碰后端。 */
    const handleDiscardDraft = useCallback(() => {
        Modal.confirm({
            title: '丢弃刚画的面?',
            content: '草稿还没保存, 丢弃后需要重新绘制。',
            okText: '丢弃',
            okButtonProps: { danger: true },
            cancelText: '继续编辑',
            onOk: () => {
                draw.clearDraft();
                setDrawerOpen(false);
                setConflictIds([]);
            },
        });
    }, [draw]);

    /* ====================== 删除 ====================== */

    const handleDelete = useCallback(() => {
        // 草稿态: "删除面" 语义是丢弃草稿, 不是删线上数据
        if (draw.draftGeometry) {
            handleDiscardDraft();
            return;
        }
        if (!selected) return;
        const { aoiId, zipcode } = selected;
        Modal.confirm({
            title: `确认删除 AOI-${aoiId}?`,
            content: '删除后该 AOI 的几何与属性均不可恢复。',
            okText: '删除',
            okButtonProps: { danger: true },
            cancelText: '取消',
            onOk: async () => {
                try {
                    const { data } = await deleteMutation({ variables: { input: { aoiId, zipcode } } });
                    const result = data?.deleteBizAoi;
                    if (!result?.success) {
                        message.error(produceErrorText(result?.errorCode, result?.errorMessage));
                        return;
                    }
                    message.success(`已删除 AOI-${aoiId}`);
                    setDrawerOpen(false);
                    select(null);
                    draw.stopEdit();
                    draw.clearEdited();
                    setConflictIds([]);
                    // 失效范围 = 被删面的原范围
                    invalidateTiles(bboxOf(selectedGeometry));
                } catch (err) {
                    message.error(`删除失败: ${err instanceof Error ? err.message : String(err)}`);
                }
            },
        });
    }, [selected, deleteMutation, select, draw, invalidateTiles, selectedGeometry]);

    /* ====================== 抽屉回调 ====================== */

    const handleDrawerClose = useCallback(() => {
        // 收起不清高亮 (selected 保留); 未保存的形状改动 / 新建草稿丢弃
        setDrawerOpen(false);
        setPickTarget(null);
        draw.cancelDraw();
        draw.stopEdit();
        draw.clearEdited();
        draw.clearDraft();
    }, [draw]);

    const handleCreated = useCallback((result: BizAoiProduceResult) => {
        // 失效范围 = 刚画的面 (要在 clearDraft 之前取)
        const createdBbox = bboxOf(draw.draftGeometry);
        draw.clearDraft();
        // 新建的面来源是 MANUAL: 「手绘」图层若被关着, 保存成功的面会直接不可见,
        // 用户的体感是"我刚建的 AOI 消失了" —— 自动打开并提示, 别让人猜
        if (!sourceVisibility.MANUAL) {
            message.info('已自动开启「手绘」图层, 以显示新建的 AOI');
            setSourceVisibility((prev) => ({ ...prev, MANUAL: true }));
        }
        if (result.aoiId != null && result.zipcode) {
            // result.aoiId 运行时是字符串 (Long 标量), 入状态前归一成数字
            const idNum = toAoiIdNumber(result.aoiId);
            // 挂载是后端异步任务: 只提示一句, 不做进度/完成状态跟踪
            // (2026-08-04 拍板: 轮询/进度条/完成提示全拆, 坑多收益小)
            // 2级挂楼栋 / 1级挂地址, 文案保持通用
            if (result.asyncTaskId != null) {
                message.info('挂载任务已提交后台执行, 稍后自动生效');
            }
            // 保留选中高亮, 但收起抽屉 (保存成功即闭环, 2026-08-03 需求)
            select({ aoiId: idNum, zipcode: result.zipcode, createdAt: null });
        }
        setDrawerOpen(false);
        invalidateTiles(createdBbox);
    }, [draw, select, invalidateTiles, sourceVisibility.MANUAL]);

    const handleSaved = useCallback((opts: { geometryChanged: boolean; statusChanged: boolean; status: number; tagsChanged: boolean }) => {
        // 失效范围 = 旧形状 ∪ 新形状 (面缩小时旧覆盖区的瓦片同样要刷, 要在 clearEdited 之前取)
        const changedBbox = bboxOfAll([draw.editedGeometry, selectedGeometry]);
        setConflictIds([]);
        draw.clearEdited();
        draw.stopEdit();
        // 保存成功收起抽屉 (选中高亮保留, 想继续改再点开)
        setDrawerOpen(false);
        // 生效状态变了, 面会在"只含已生效"/"含未生效"两套瓦片之间进出, 同样要按范围失效
        if (opts.geometryChanged || opts.statusChanged) invalidateTiles(changedBbox);
        if (opts.statusChanged && opts.status === 0 && !showUnverified) {
            message.info('该 AOI 已置为未生效; 打开图例里的「未生效」图层可继续在地图上查看');
        }
        // 标签变了: 列表的 chips / 按标签筛出的结果要跟着变, 查过就重跑一次当前筛选
        if (opts.tagsChanged && searched) handleSearch();
    }, [draw, invalidateTiles, selectedGeometry, showUnverified, searched, handleSearch]);

    const handleConflict = useCallback((ids: number[]) => {
        // 只标红, 不再 fitBounds: 冲突面的几何以前是从"整屏已加载的 features"里捞的,
        // 换成瓦片后前端手里没有几何 (瓦片是渲染用的二进制, 不是数据源)。
        // 冲突面就在用户刚画的位置附近, 标红足够定位, 不值得为此再拉一次详情。
        // 归一成数字: 响应里的 Long 是字符串, 瓦片属性里的 aoiId 是数字, 不转则 Set.has 永远 miss (不标红)
        setConflictIds(ids.map(toAoiIdNumber));
    }, []);

    const handlePickPoint = useCallback((lng: number, lat: number) => {
        pickNonceRef.current += 1;
        setPickedPoint({ lng, lat, nonce: pickNonceRef.current });
    }, []);

    const handlePointsChange = useCallback((entry: DrawerPoint[], parking: DrawerPoint[]) => {
        setDrawerEntryPoints(entry);
        setDrawerParkingPoints(parking);
    }, []);


    /* ====================== 派生 ====================== */

    const drawerMode: 'create' | 'edit' = draw.draftGeometry ? 'create' : 'edit';
    const drawerVisible = draw.draftGeometry != null || (drawerOpen && selected != null);

    // 不变式: 选点态只在抽屉打开时有意义。抽屉从任何路径收起 (保存成功自动收起 / 手动关闭 / 丢弃草稿)
    // 都必须退出选点, 否则"标记停车点"提示条常驻、地图点击被"选点中"守卫吞掉, 建筑再也点不动。
    // 用 effect 兜底而不是在每个收起路径里手动清, 是为了让将来新增的收起路径不可能再漏。
    useEffect(() => {
        if (!drawerVisible) {
            setPickTarget(null);
        }
    }, [drawerVisible]);

    // edit 模式的操作对象: 草稿态编辑草稿本身 (它还没落库), 否则编辑选中面 (pending 改动优先)
    const editGeometry = draw.draftGeometry ?? draw.editedGeometry ?? selectedGeometry;

    const mapEntryPoints = drawerVisible ? drawerEntryPoints : [];
    const mapParkingPoints = drawerVisible ? drawerParkingPoints : [];

    const toggleSource = useCallback((group: SourceGroup) => {
        setSourceVisibility((prev) => ({ ...prev, [group]: !prev[group] }));
    }, []);

    const toggleUnverified = useCallback(() => {
        setShowUnverified((v) => !v);
    }, []);

    /* ====================== 渲染 ====================== */

    return (
        <div
            className="flex flex-col"
            style={fullscreen
                // 全屏: 脱离页面流铺满视口 (盖住左侧菜单)。zIndex 低于 antd Modal(1000+) 之外的浮层无妨,
                // 取 900 保证盖住页面内容又不压住全局 message/Modal。
                ? { position: 'fixed', inset: 0, zIndex: 900, background: '#f0f2f5', padding: 12 }
                : { flex: 1, minHeight: 0, overflow: 'hidden', padding: 24 }}
        >
            {/* 页面标题行 ("数据服务正常"绿标与"新建 AOI"按钮均已按需求移除; 新建入口 = 地图工具栏"绘制 AOI") */}
            {chromeVisible && (
                <div className="mb-4 flex items-end justify-between">
                    <h4 className="text-xl font-bold">AOI 生产管理</h4>
                </div>
            )}

            {/* 筛选区 */}
            {chromeVisible && (
                <FilterBar
                    filters={filters}
                    onChange={setFilters}
                    onSearch={handleSearch}
                    onReset={handleReset}
                    onFlyToCoord={handleFlyToCoord}
                    loading={listLoading}
                    tagOptions={tagDictionary}
                />
            )}

            {/* 列表 + 地图 + 抽屉 (整体一张白卡, 与项目其他页面的 bg-white rounded-lg shadow-sm 一致) */}
            <div
                className="flex overflow-hidden rounded-lg bg-white shadow-sm"
                style={{ flex: 1, minHeight: 0, position: 'relative', marginTop: chromeVisible ? 16 : 0 }}
            >
                {chromeVisible && (
                <AoiListPanel
                    items={listItems}
                    total={listTotal}
                    loading={listLoading}
                    searched={searched}
                    selectedAoiId={selected?.aoiId ?? null}
                    collapsed={listCollapsed}
                    onToggleCollapsed={() => setListCollapsed((c) => !c)}
                    onItemClick={handleListItemClick}
                    tagNames={tagNames}
                />
                )}

                <div style={{ flex: 1, position: 'relative', minWidth: 0, background: '#f0f2f5' }}>
                    <MapCanvas
                        basemap={basemap}
                        onBasemapChange={setBasemap}
                        tileVersion={tileInvalidation.version}
                        tileInvalidationBbox={tileInvalidation.bbox}
                        sourceVisibility={sourceVisibility}
                        showUnverified={showUnverified}
                        selectedAoiId={selected?.aoiId ?? null}
                        // 编辑形状点"完成编辑"后改动是 pending 的 (保存才落库), 高亮层要显示改后的形状,
                        // 否则表现为"完成编辑了地图上还是老形状"
                        selectedGeometry={draw.editedGeometry ?? selectedGeometry}
                        selectedLevelEnum={numericLevelToEnum(detail?.level ?? null)}
                        coordMarker={coordMarker}
                        fullscreen={fullscreen}
                        onToggleFullscreen={() => setFullscreen((v) => !v)}
                        fullscreenChrome={fullscreenChrome}
                        onToggleFullscreenChrome={() => setFullscreenChrome((v) => !v)}
                        suppressSelectedInTiles={draw.mode === 'edit' || draw.editedGeometry != null}
                        conflictIds={conflictIds}
                        drawMode={draw.mode}
                        editGeometry={editGeometry}
                        draftGeometry={draw.draftGeometry}
                        pickTarget={pickTarget}
                        entryPoints={mapEntryPoints}
                        parkingPoints={mapParkingPoints}
                        viewCommand={viewCommand}
                        onViewportChange={(vp) => setMapZoom(vp.zoom)}
                        onSelectAoi={(aoiId, zipcode) => handleSelectAoi(aoiId, zipcode)}
                        onPickPoint={handlePickPoint}
                        onDrawComplete={handleDrawComplete}
                        onEditChange={handleEditChange}
                        drawActionsRef={drawActionsRef}
                        onDrawVertexCountChange={setDrawVertexCount}
                        overlayRightOffset={drawerVisible ? DRAWER_WIDTH + 12 : 12}
                    >
                        <HasResource resourceCodes={[ResourceCode.aoiPlatform.UPSERT]}>
                            <MapToolbar
                                offsetLeft={listCollapsed ? 42 : 12}
                                drawMode={draw.mode}
                                hasSelection={selected != null}
                                hasDraft={draw.draftGeometry != null}
                                drawVertexCount={drawVertexCount}
                                onStartDraw={handleStartDraw}
                                onToggleEditShape={handleToggleEditShape}
                                onDeleteSelected={handleDelete}
                                onUndoVertex={handleUndoVertex}
                                onFinishDraw={handleFinishDraw}
                            />
                        </HasResource>
                        <Legend
                            visibility={sourceVisibility}
                            onToggle={toggleSource}
                            zoom={mapZoom}
                            showUnverified={showUnverified}
                            onToggleUnverified={toggleUnverified}
                        />
                        {draw.mode === 'draw' && (
                            <HintPill>
                                单击地图逐点绘制，双击闭合完成 AOI 面
                                <Button type="link" size="small" className="!px-0" onClick={handleStartDraw}>
                                    取消
                                </Button>
                            </HintPill>
                        )}
                        {draw.mode === 'edit' && (
                            <HintPill>
                                拖动顶点 / 边中点调整形状，右键顶点删除；完成后到抽屉保存
                            </HintPill>
                        )}
                        {draw.mode === 'idle' && pickTarget && (
                            <HintPill>在地图上单击，标记{pickTarget === 'entry' ? '进门点' : '停车点'}位置</HintPill>
                        )}
                    </MapCanvas>

                    {drawerVisible && (
                        <AoiDrawer
                            mode={drawerMode}
                            detail={drawerMode === 'edit' ? detail : null}
                            detailLoading={detailLoading}
                            createdAtText={detail?.aoiCreatedAt ?? selected?.createdAt ?? null}
                            draftGeometry={draw.draftGeometry}
                            editedGeometry={draw.editedGeometry}
                            isEditingShape={draw.mode === 'edit'}
                            canEdit={canEdit}
                            tagDictionary={tagDictionary}
                            tagDictionaryError={!!tagDictError && tagDictionary.length === 0}
                            onRetryTagDictionary={() => { void refetchTagDict(); }}
                            pickTarget={pickTarget}
                            onPickTargetChange={setPickTarget}
                            pickedPoint={pickedPoint}
                            onPointsChange={handlePointsChange}
                            onClose={handleDrawerClose}
                            onDelete={handleDelete}
                            onToggleEditShape={handleToggleEditShape}
                            onCreated={handleCreated}
                            onSaved={handleSaved}
                            onConflict={handleConflict}
                            refetchDetail={refetchDetail}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default AoiPlatformV2Page;
